// externals
import type { MiddlewareAPI } from 'redux';
import { LOCATION_CHANGE } from 'connected-react-router';

// libraries
import { isAppWhitelisted } from '@makemydeal/dr-sales-checkin-ui-utils';
import type { SocketEventPayload } from '@makemydeal/dr-shared-bff-types';
import { FEATURES, routingConfig } from '@makemydeal/dr-sales-checkin-types';
import { DealXgTopic } from '@makemydeal/dr-shared-types/constants/dealXgTopics';
import { NavigationActions } from '@makemydeal/dr-activities-common';

// store
import { getDealRefreshAppNameWhitelist } from '../dealRefresh/getDealRefreshAppNameWhitelist';
import { isDealRefreshModalVisible } from '../dealRefresh/selector';
import { isFeatureVisible } from '../capabilities/selector';
import { isCustomerCardOpen } from '../common/selector';
import { isTradeInCompleted } from '../tradeIn/selector';
import { isFullPageScrollEnabled } from '../showroomNav/selector';
import { deferDealRefreshModal } from './dealRefreshDeferActions';
import { isDealRefreshDeferPending } from './selector';
import { showDealRefreshModal } from '../dealRefresh/dealRefreshActionCreators';
import type { DealRefreshDeferGateStateTree } from './gateStateTree';

const TRADE_IN_ROUTE = routingConfig.TRADE_IN;
const FINANCING_ROUTE = routingConfig.FINANCING;

/**
 * Edition gate (F303238) — deal refresh rides the `gtl` capability. Read directly rather
 * than injected: unlike the shared `dealRefreshMiddleware`, this module is already
 * sales-checkin-specific (trade-in route, app whitelist), so there is no host to configure.
 * Both halves check it — gating only the release would leave a stale pending defer in the
 * slice, and gating only the defer would let a defer taken while the capability was on
 * surface after it turned off.
 */
const isDealRefreshEnabled = isFeatureVisible(FEATURES.GTL);

const getPathname = (state: DealRefreshDeferGateStateTree): string => state.router?.location?.pathname ?? '';

const isMidTradeIn = (state: DealRefreshDeferGateStateTree, pathnameOverride?: string): boolean => {
    const pathname = pathnameOverride ?? getPathname(state);

    if (!pathname.startsWith(TRADE_IN_ROUTE)) return false;

    // Under full-scroll the inline Trade-In section never pushes this pathname — only its
    // takeover does, as a sub-route beneath it — so reaching here at all means the takeover is
    // open, whatever the trade-in's own completion state (FR-052, TA4265943). Toggle off is
    // unchanged: that pathname is the standalone screen itself, where completion still
    // distinguishes "mid entry" from "done, just viewing it."
    return isFullPageScrollEnabled(state) || !isTradeInCompleted(state);
};

const isOnFinancingRoute = (state: DealRefreshDeferGateStateTree, pathnameOverride?: string): boolean => {
    const pathname = pathnameOverride ?? getPathname(state);
    return pathname.startsWith(FINANCING_ROUTE);
};

/**
 * Every "the shopper would lose unsaved work" gate GTL defers for. `pathnameOverride` lets a
 * caller supply the NAVIGATE_TO destination directly (see `releaseDeferredRefresh`) rather than
 * reading `state.router.location.pathname`, which is not guaranteed to have settled to the new
 * route yet when this runs on the way out.
 */
const isAnyGateActive = (state: DealRefreshDeferGateStateTree, pathnameOverride?: string): boolean =>
    isMidTradeIn(state, pathnameOverride) || isOnFinancingRoute(state, pathnameOverride) || isCustomerCardOpen(state);

/**
 * While the shopper is on one of the gated screens, defer the shared DealRefreshModal
 * and swallow the socket event so an incoming deal-refresh does not interrupt them. Mirrors the
 * Sales View (client) behavior.
 *
 * @returns `true` when the event was swallowed (caller must NOT forward it downstream).
 */
export const deferDealRefresh = (
    store: MiddlewareAPI<any, DealRefreshDeferGateStateTree>,
    action: { type: string; payload?: any }
): boolean => {
    const state = store.getState();
    // SCI should only care about create topics.
    const shouldDefer =
        isDealRefreshEnabled(state) &&
        action.payload?.topic === DealXgTopic.DEAL_XG_CREATE &&
        isAppWhitelisted(action.payload as SocketEventPayload, getDealRefreshAppNameWhitelist(state)) &&
        !isDealRefreshModalVisible(state) &&
        isAnyGateActive(state);

    if (!shouldDefer) return false;

    store.dispatch(deferDealRefreshModal());
    return true;
};

/**
 * Releases a deferred refresh once every active gate above has cleared — e.g. the shopper
 * navigates away from Trade-In/Financing, or closes the Customer Details slideout — showing the
 * DealRefreshModal that was swallowed while they were mid edit. Re-checks the full gate set rather
 * than just the one that changed, so e.g. closing the slideout while still mid Trade-In does not
 * release early.
 *
 * Under full-scroll, leaving a takeover ("Back to My Deal" or browser Back) is a history pop —
 * there is no `NAVIGATE_TO` to release on, only the router's own `LOCATION_CHANGE` (TA4265943).
 * Gated here rather than on the trigger in `dealRefreshDeferMiddleware`, so toggle off — where
 * every address still gets its own `NAVIGATE_TO` — keeps exactly today's release.
 *
 * `isDealRefreshEnabled`/`isDealRefreshDeferPending` must run before the full-scroll read:
 * `isSciFullPageScrollEnabled` throws if read before bootstrap completes, and the router's own
 * `LOCATION_CHANGE` fires that early (syncing to the starting URL) on every load, pending or not.
 * Nothing is ever pending that early, so bailing out first means the throwing read is never
 * reached then.
 */
export const releaseDeferredRefresh = (
    store: MiddlewareAPI<any, DealRefreshDeferGateStateTree>,
    action: { type: string; payload?: any }
): void => {
    const state = store.getState();

    if (!isDealRefreshEnabled(state) || !isDealRefreshDeferPending(state)) {
        return;
    }

    if (action.type === LOCATION_CHANGE && !isFullPageScrollEnabled(state)) {
        return;
    }

    const nextPathname: string | undefined =
        action.type === NavigationActions.NAVIGATE_TO
            ? action.payload ?? ''
            : action.type === LOCATION_CHANGE
            ? action.payload?.location?.pathname
            : undefined;

    if (!isAnyGateActive(state, nextPathname)) {
        store.dispatch(showDealRefreshModal());
    }
};
